import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../../services/api';
import {
  PlusIcon,
  BuildingOfficeIcon,
  MagnifyingGlassIcon,
  GlobeAltIcon,
  EnvelopeIcon,
  PhoneIcon,
  XMarkIcon,
  MapPinIcon,
  CheckCircleIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

  .ss-root * { box-sizing: border-box; }

  .ss-root {
    font-family: 'Sora', sans-serif;
    background: #f0f2f7;
    min-height: 100vh;
    color: #1a1d2e;
    padding: 2rem;
  }

  /* HEADER */
  .ss-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.75rem;
    flex-wrap: wrap;
    gap: 1rem;
  }

  .ss-title {
    font-size: 1.75rem;
    font-weight: 700;
    letter-spacing: -0.03em;
    color: #0f1123;
  }

  .ss-subtitle {
    font-size: 0.82rem;
    color: #8b90a7;
    margin-top: 2px;
    font-weight: 400;
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  /* SEARCH */
  .search-wrap {
    position: relative;
    display: flex;
    align-items: center;
  }

  .search-wrap svg {
    position: absolute;
    left: 12px;
    width: 15px; height: 15px;
    color: #9094a8;
    stroke-width: 2;
    pointer-events: none;
  }

  .search-input {
    font-family: 'Sora', sans-serif;
    font-size: 0.82rem;
    padding: 9px 14px 9px 36px;
    border: 1.5px solid #e4e7f0;
    border-radius: 10px;
    background: white;
    color: #1a1d2e;
    outline: none;
    width: 220px;
    transition: border-color 0.2s, box-shadow 0.2s;
  }

  .search-input:focus {
    border-color: #3b6ef8;
    box-shadow: 0 0 0 3px rgba(59,110,248,0.1);
  }

  .search-input::placeholder { color: #c0c5dc; }

  /* BUTTON */
  .btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 10px 18px;
    background: #3b6ef8;
    color: white;
    border: none;
    border-radius: 10px;
    font-family: 'Sora', sans-serif;
    font-size: 0.82rem;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
  }

  .btn-primary:hover {
    background: #2d5de0;
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(59,110,248,0.3);
  }

  .btn-primary svg { width: 16px; height: 16px; stroke-width: 2.2; }

  /* SUMMARY PILLS */
  .summary-row {
    display: flex;
    gap: 10px;
    margin-bottom: 1.25rem;
    flex-wrap: wrap;
  }

  .summary-pill {
    display: flex;
    align-items: center;
    gap: 8px;
    background: white;
    border: 1px solid #e8ebf4;
    border-radius: 10px;
    padding: 8px 16px;
    font-size: 0.78rem;
    color: #6b7194;
    font-weight: 500;
  }

  .summary-pill strong {
    color: #0f1123;
    font-size: 0.88rem;
    font-weight: 700;
  }

  .pill-dot {
    width: 8px; height: 8px;
    border-radius: 50%;
  }

  /* TABLE PANEL */
  .table-panel {
    background: white;
    border-radius: 16px;
    border: 1px solid #e8ebf4;
    overflow: hidden;
    animation: fadeUp 0.4s ease both;
  }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* TABLE */
  .schools-table {
    width: 100%;
    border-collapse: collapse;
  }

  .schools-table thead {
    background: #fafbff;
    border-bottom: 1px solid #edf0f9;
  }

  .schools-table th {
    padding: 12px 20px;
    text-align: left;
    font-size: 0.7rem;
    font-weight: 600;
    color: #9094a8;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    white-space: nowrap;
  }

  .schools-table tbody tr {
    border-bottom: 1px solid #f3f5fb;
    transition: background 0.15s;
  }

  .schools-table tbody tr:last-child { border-bottom: none; }
  .schools-table tbody tr:hover { background: #fafbff; }

  .schools-table td {
    padding: 14px 20px;
    vertical-align: middle;
  }

  /* SCHOOL NAME CELL */
  .school-name-cell {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .school-avatar {
    width: 36px; height: 36px;
    border-radius: 10px;
    background: #eef2ff;
    color: #3b6ef8;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }

  .school-avatar svg { width: 18px; height: 18px; stroke-width: 1.8; }

  .school-name-text {
    font-size: 0.85rem;
    font-weight: 600;
    color: #1a1d2e;
  }

  /* MONO CELL */
  .mono-cell {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.78rem;
    color: #6b7194;
  }

  /* CONTACT CELL */
  .contact-cell {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.82rem;
    color: #6b7194;
  }

  .contact-cell svg {
    width: 13px; height: 13px;
    color: #b0b5cc;
    stroke-width: 1.8;
    flex-shrink: 0;
  }

  /* STATUS BADGE */
  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 0.68rem;
    font-weight: 600;
    padding: 3px 10px;
    border-radius: 99px;
    background: #ecfdf5;
    color: #10c98e;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .status-dot {
    width: 5px; height: 5px;
    border-radius: 50%;
    background: #10c98e;
  }

  /* MANAGE LINK */

  .btn-toggle-school { width:28px; height:28px; border-radius:7px; cursor:pointer; display:inline-flex; align-items:center; justify-content:center; border:1px solid; font-size:.72rem; font-weight:700; transition:all .15s; flex-shrink:0; }
  .btn-toggle-school svg { width:13px; height:13px; stroke-width:2; }
  .btn-toggle-school.deactivate { color:#ef4444; border-color:#fecaca; background:#fef2f2; }
  .btn-toggle-school.deactivate:hover { background:#ef4444; color:white; border-color:#ef4444; }
  .btn-toggle-school.activate   { color:#10c98e; border-color:#a7f3d0; background:#ecfdf5; }
  .btn-toggle-school.activate:hover   { background:#10c98e; color:white; border-color:#10c98e; }
  .btn-toggle-school:disabled { opacity:.5; cursor:not-allowed; }
  .manage-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.78rem;
    font-weight: 600;
    color: #3b6ef8;
    text-decoration: none;
    padding: 5px 12px;
    border-radius: 8px;
    background: #eef2ff;
    transition: background 0.15s, color 0.15s;
  }

  .manage-link:hover {
    background: #3b6ef8;
    color: white;
  }

  /* EMPTY STATE */
  .empty-state {
    text-align: center;
    padding: 4rem 2rem;
  }

  .empty-icon {
    width: 56px; height: 56px;
    border-radius: 16px;
    background: #eef2ff;
    color: #3b6ef8;
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 16px;
  }

  .empty-icon svg { width: 26px; height: 26px; stroke-width: 1.6; }

  .empty-title {
    font-size: 0.95rem;
    font-weight: 600;
    color: #1a1d2e;
    margin-bottom: 6px;
  }

  .empty-sub {
    font-size: 0.78rem;
    color: #9094a8;
    margin-bottom: 20px;
  }

  /* MODAL */
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 17, 35, 0.55);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 50;
    animation: overlayIn 0.2s ease;
  }

  @keyframes overlayIn { from { opacity: 0; } to { opacity: 1; } }

  .modal-card {
    background: white;
    border-radius: 20px;
    padding: 2rem;
    width: 100%;
    max-width: 460px;
    margin: 1rem;
    border: 1px solid #e8ebf4;
    animation: modalIn 0.25s ease;
    max-height: 90vh;
    overflow-y: auto;
  }

  @keyframes modalIn {
    from { opacity: 0; transform: translateY(16px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.5rem;
  }

  .modal-title {
    font-size: 1.05rem;
    font-weight: 700;
    color: #0f1123;
    letter-spacing: -0.02em;
  }

  .modal-sub {
    font-size: 0.76rem;
    color: #9094a8;
    margin-top: 2px;
  }

  .modal-close {
    width: 32px; height: 32px;
    border-radius: 8px;
    border: 1px solid #e8ebf4;
    background: white;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    color: #9094a8;
    transition: background 0.15s, color 0.15s;
    flex-shrink: 0;
  }

  .modal-close:hover { background: #f3f5fb; color: #1a1d2e; }
  .modal-close svg { width: 16px; height: 16px; }

  /* FORM */
  .field { margin-bottom: 1rem; }

  .field label {
    display: block;
    font-size: 0.75rem;
    font-weight: 500;
    color: #6b7194;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 6px;
  }

  .field input,
  .field textarea {
    width: 100%;
    padding: 10px 14px;
    border: 1.5px solid #e4e7f0;
    border-radius: 10px;
    font-family: 'Sora', sans-serif;
    font-size: 0.84rem;
    color: #1a1d2e;
    background: #fafbff;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
    resize: vertical;
  }

  .field input:focus,
  .field textarea:focus {
    border-color: #3b6ef8;
    box-shadow: 0 0 0 3px rgba(59, 110, 248, 0.1);
    background: white;
  }

  .field input::placeholder,
  .field textarea::placeholder { color: #c0c5dc; }

  .field-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 1.5rem;
  }

  .btn-ghost {
    padding: 10px 18px;
    background: #f3f5fb;
    border: none;
    border-radius: 10px;
    font-family: 'Sora', sans-serif;
    font-size: 0.82rem;
    font-weight: 500;
    color: #6b7194;
    cursor: pointer;
    transition: background 0.15s;
  }

  .btn-ghost:hover { background: #e8ebf4; }

  /* LOADING */
  .loading-wrap {
    display: flex; align-items: center; justify-content: center;
    height: 60vh;
    color: #9094a8; font-size: 0.9rem; gap: 10px;
  }

  .spinner {
    width: 18px; height: 18px;
    border: 2px solid #e4e7f0;
    border-top-color: #3b6ef8;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }

  @keyframes spin { to { transform: rotate(360deg); } }

  /* Mobile cards */
  .ss-cards { display:none; padding:10px; }
  .ss-card { background:white; border-radius:12px; border:1px solid #e8ebf4; padding:13px; margin-bottom:8px; }
  .ss-card-top { display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom:8px; }
  .ss-card-row { display:flex; justify-content:space-between; align-items:center; font-size:.78rem; padding:5px 0; border-bottom:1px solid #f3f5fb; gap:8px; }
  .ss-card-row:last-of-type { border-bottom:none; }
  .ss-card-label { color:#9094a8; font-weight:600; font-size:.68rem; text-transform:uppercase; letter-spacing:.05em; flex-shrink:0; }
  .ss-card-actions { display:flex; gap:6px; margin-top:10px; align-items:center; }
  @media (max-width:768px) {
    .ss-tbl-wrap { display:none !important; }
    .ss-cards { display:block; }
  }
`;

const EMPTY_FORM = { name: '', subdomain: '', address: '', phone: '', email: '', rolePackage: '', cloPloEnabled: false };

const PACKAGE_OPTIONS = [
  { value: 'package1', label: 'Package 1 — Owner, Admin, Teacher' },
  { value: 'package2', label: 'Package 2 — Owner, Admin, Teacher, Student' },
  { value: 'package3', label: 'Package 3 — Owner, Admin, Coordinator, Finance, Teacher, Student, Parent, Library, Hostel' },
];

const SuperSchools = () => {
  const [schools, setSchools]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toggling,   setToggling]   = useState({});
  const [search, setSearch]       = useState('');
  const [formData, setFormData]   = useState(EMPTY_FORM);

  useEffect(() => { fetchSchools(); }, []);

  const fetchSchools = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/super/schools');
      setSchools(data);
    } catch {
      toast.error('Failed to fetch schools');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/super/schools', formData);
      toast.success('School created successfully');
      setShowModal(false);
      setFormData(EMPTY_FORM);
      fetchSchools();
    } catch {
      toast.error('Failed to create school');
    } finally {
      setSubmitting(false);
    }
  };


  const handleToggle = async (schoolId, currentActive) => {
    setToggling(p => ({...p, [schoolId]: true}));
    try {
      await api.put(`/super/schools/${schoolId}`, { isActive: !currentActive });
      toast.success(`School ${!currentActive ? 'activated' : 'deactivated'}`);
      fetchSchools();
    } catch { toast.error('Failed to update'); }
    finally { setToggling(p => ({...p, [schoolId]: false})); }
  };

  const handlePackageChange = async (schoolId, rolePackage) => {
    const prev = schools;
    setSchools(s => s.map(sc => sc._id === schoolId ? { ...sc, rolePackage } : sc));
    try {
      await api.put(`/super/schools/${schoolId}/role-package`, { rolePackage });
      toast.success('Package updated');
    } catch {
      setSchools(prev);
      toast.error('Failed to update package');
    }
  };

  const MODULE_FIELD = { library: 'libraryEnabled', hostel: 'hostelEnabled', cloPlo: 'cloPloEnabled' };
  const MODULE_LABEL = { library: 'Library', hostel: 'Hostel', cloPlo: 'CLO/PLO' };

  const handleModuleToggle = async (schoolId, moduleKey, currentValue) => {
    const field = MODULE_FIELD[moduleKey];
    const prev = schools;
    setSchools(s => s.map(sc => sc._id === schoolId ? { ...sc, [field]: !currentValue } : sc));
    try {
      await api.put(`/super/schools/${schoolId}/modules`, { [field]: !currentValue });
      toast.success(`${MODULE_LABEL[moduleKey]} module ${!currentValue ? 'enabled' : 'disabled'}`);
    } catch {
      setSchools(prev);
      toast.error('Failed to update module');
    }
  };

  const filtered = schools.filter((s) =>
    s.name?.toLowerCase().includes(search.toLowerCase()) ||
    s.subdomain?.toLowerCase().includes(search.toLowerCase()) ||
    s.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return (
    <div className="ss-root">
      <style>{style}</style>
      <div className="loading-wrap"><div className="spinner" /> Loading schools…</div>
    </div>
  );

  return (
    <div className="ss-root">
      <style>{style}</style>

      {/* HEADER */}
      <div className="ss-header">
        <div>
          <h1 className="ss-title">Schools</h1>
          <p className="ss-subtitle">Manage all institutions on the platform.</p>
        </div>
        <div className="header-actions">
          <div className="search-wrap">
            <MagnifyingGlassIcon />
            <input
              className="search-input"
              placeholder="Search schools…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button className="btn-primary" onClick={() => setShowModal(true)}>
            <PlusIcon /> Add School
          </button>
        </div>
      </div>

      {/* SUMMARY PILLS */}
      <div className="summary-row">
        <div className="summary-pill">
          <span className="pill-dot" style={{ background: '#3b6ef8' }} />
          Total <strong>{schools.length}</strong> schools
        </div>
        <div className="summary-pill">
          <span className="pill-dot" style={{ background: '#10c98e' }} />
          <strong>{schools.filter(s => s.isActive !== false).length}</strong> active
        </div>
        {search && (
          <div className="summary-pill">
            <span className="pill-dot" style={{ background: '#8b5cf6' }} />
            <strong>{filtered.length}</strong> results for "{search}"
          </div>
        )}
      </div>

      {/* TABLE */}
      <div className="table-panel">
        <div className="ss-tbl-wrap">
        <table className="schools-table">
          <thead>
            <tr>
              <th style={{textAlign:'center'}}>School</th>
              <th style={{textAlign:'center'}}>Subdomain</th>
              <th style={{textAlign:'center'}}>Email</th>
              <th style={{textAlign:'center'}}>Phone</th>
              <th style={{textAlign:'center'}}>Package</th>
              <th style={{textAlign:'center'}}>Modules</th>
              <th style={{textAlign:'center'}}>Status</th>
              <th style={{textAlign:'center'}}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <div className="empty-state">
                    <div className="empty-icon"><BuildingOfficeIcon /></div>
                    <div className="empty-title">
                      {search ? 'No schools match your search' : 'No schools yet'}
                    </div>
                    <div className="empty-sub">
                      {search ? 'Try a different keyword.' : 'Get started by adding your first school.'}
                    </div>
                    {!search && (
                      <button className="btn-primary" onClick={() => setShowModal(true)}>
                        <PlusIcon /> Add School
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              filtered.map((school) => (
                <tr key={school._id}>
                  <td style={{textAlign:'center'}}>
                    <div className="school-name-cell">
                      <div className="school-avatar"><BuildingOfficeIcon /></div>
                      <span className="school-name-text">{school.name}</span>
                    </div>
                  </td>
                  <td style={{textAlign:'center'}}>
                    <div className="contact-cell">
                      <GlobeAltIcon />
                      <span className="mono-cell">{school.subdomain}</span>
                    </div>
                  </td>
                  <td style={{textAlign:'center'}}>
                    <div className="contact-cell">
                      <EnvelopeIcon />
                      {school.email || <span style={{ color: '#c0c5dc' }}>—</span>}
                    </div>
                  </td>
                  <td style={{textAlign:'center'}}>
                    <div className="contact-cell">
                      <PhoneIcon />
                      <span className="mono-cell">{school.phone || <span style={{ color: '#c0c5dc' }}>—</span>}</span>
                    </div>
                  </td>
                  <td style={{textAlign:'center'}}>
                    <select
                      value={school.rolePackage || 'package3'}
                      onChange={(e) => handlePackageChange(school._id, e.target.value)}
                      style={{fontFamily:'Sora,sans-serif', fontSize:'.74rem', padding:'6px 8px', borderRadius:8, border:'1.5px solid #e4e7f0', background:'white', color:'#1a1d2e', outline:'none', cursor:'pointer'}}
                      title={PACKAGE_OPTIONS.find(o => o.value === (school.rolePackage||'package3'))?.label}
                    >
                      {PACKAGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.value === 'package1' ? 'Package 1' : o.value === 'package2' ? 'Package 2' : 'Package 3'}</option>)}
                    </select>
                  </td>
                  <td style={{textAlign:'center'}}>
                    <div style={{display:'flex', gap:6, justifyContent:'center'}}>
                      <button
                        onClick={() => handleModuleToggle(school._id, 'library', !!school.libraryEnabled)}
                        title={school.libraryEnabled ? 'Library module ON — click to disable' : 'Library module OFF — click to enable'}
                        style={{fontFamily:'Sora,sans-serif', fontSize:'.68rem', fontWeight:600, padding:'4px 8px', borderRadius:99, cursor:'pointer',
                          background: school.libraryEnabled ? '#ecfdf5' : '#f0f2f7',
                          color:      school.libraryEnabled ? '#10c98e' : '#9094a8',
                          border:     `1px solid ${school.libraryEnabled ? '#a7f3d0' : '#e4e7f0'}`}}>
                        Library
                      </button>
                      <button
                        onClick={() => handleModuleToggle(school._id, 'hostel', !!school.hostelEnabled)}
                        title={school.hostelEnabled ? 'Hostel module ON — click to disable' : 'Hostel module OFF — click to enable'}
                        style={{fontFamily:'Sora,sans-serif', fontSize:'.68rem', fontWeight:600, padding:'4px 8px', borderRadius:99, cursor:'pointer',
                          background: school.hostelEnabled ? '#ecfdf5' : '#f0f2f7',
                          color:      school.hostelEnabled ? '#10c98e' : '#9094a8',
                          border:     `1px solid ${school.hostelEnabled ? '#a7f3d0' : '#e4e7f0'}`}}>
                        Hostel
                      </button>
                      <button
                        onClick={() => handleModuleToggle(school._id, 'cloPlo', !!school.cloPloEnabled)}
                        title={school.cloPloEnabled ? 'CLO/PLO module ON — click to disable' : 'CLO/PLO module OFF — click to enable'}
                        style={{fontFamily:'Sora,sans-serif', fontSize:'.68rem', fontWeight:600, padding:'4px 8px', borderRadius:99, cursor:'pointer',
                          background: school.cloPloEnabled ? '#ecfdf5' : '#f0f2f7',
                          color:      school.cloPloEnabled ? '#10c98e' : '#9094a8',
                          border:     `1px solid ${school.cloPloEnabled ? '#a7f3d0' : '#e4e7f0'}`}}>
                        CLO/PLO
                      </button>
                    </div>
                  </td>
                  <td style={{textAlign:'center'}}>
                    <span className="status-badge" style={{
                      background: school.isActive!==false ? '#ecfdf5' : '#fef2f2',
                      color:      school.isActive!==false ? '#10c98e' : '#ef4444',
                      border:     `1px solid ${school.isActive!==false ? '#a7f3d0' : '#fecaca'}`,
                    }}>
                      <span className="status-dot" style={{background: school.isActive!==false ? '#10c98e' : '#ef4444'}} />
                      {school.isActive!==false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{textAlign:'center'}}>
                    <div style={{display:'flex',alignItems:'center',gap:6}}>
                      <button
                        className={`btn-toggle-school ${school.isActive!==false?'deactivate':'activate'}`}
                        onClick={() => handleToggle(school._id, school.isActive!==false)}
                        disabled={toggling[school._id]}
                        title={school.isActive!==false ? 'Deactivate School' : 'Activate School'}>
                        {toggling[school._id] ? '…' : school.isActive!==false ? <NoSymbolIcon /> : <CheckCircleIcon />}
                      </button>
                      <Link to={`/super/schools/${school._id}`} className="manage-link">
                        Manage →
                      </Link>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>

        {/* Mobile cards */}
        <div className="ss-cards">
          {filtered.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon"><BuildingOfficeIcon /></div>
              <div className="empty-title">
                {search ? 'No schools match your search' : 'No schools yet'}
              </div>
              <div className="empty-sub">
                {search ? 'Try a different keyword.' : 'Get started by adding your first school.'}
              </div>
              {!search && (
                <button className="btn-primary" onClick={() => setShowModal(true)}>
                  <PlusIcon /> Add School
                </button>
              )}
            </div>
          ) : filtered.map((school) => (
            <div className="ss-card" key={school._id}>
              <div className="ss-card-top">
                <div className="school-name-cell">
                  <div className="school-avatar"><BuildingOfficeIcon /></div>
                  <span className="school-name-text">{school.name}</span>
                </div>
                <span className="status-badge" style={{
                  background: school.isActive!==false ? '#ecfdf5' : '#fef2f2',
                  color:      school.isActive!==false ? '#10c98e' : '#ef4444',
                  border:     `1px solid ${school.isActive!==false ? '#a7f3d0' : '#fecaca'}`,
                }}>
                  <span className="status-dot" style={{background: school.isActive!==false ? '#10c98e' : '#ef4444'}} />
                  {school.isActive!==false ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="ss-card-row"><span className="ss-card-label">Subdomain</span><span className="mono-cell">{school.subdomain}</span></div>
              <div className="ss-card-row"><span className="ss-card-label">Email</span><span>{school.email || <span style={{ color: '#c0c5dc' }}>—</span>}</span></div>
              <div className="ss-card-row"><span className="ss-card-label">Phone</span><span className="mono-cell">{school.phone || <span style={{ color: '#c0c5dc' }}>—</span>}</span></div>
              <div className="ss-card-row">
                <span className="ss-card-label">Package</span>
                <select
                  value={school.rolePackage || 'package3'}
                  onChange={(e) => handlePackageChange(school._id, e.target.value)}
                  style={{fontFamily:'Sora,sans-serif', fontSize:'.74rem', padding:'6px 8px', borderRadius:8, border:'1.5px solid #e4e7f0', background:'white', color:'#1a1d2e', outline:'none'}}
                >
                  {PACKAGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.value === 'package1' ? 'Package 1' : o.value === 'package2' ? 'Package 2' : 'Package 3'}</option>)}
                </select>
              </div>
              <div className="ss-card-row">
                <span className="ss-card-label">Modules</span>
                <div style={{display:'flex', gap:6}}>
                  <button
                    onClick={() => handleModuleToggle(school._id, 'library', !!school.libraryEnabled)}
                    style={{fontFamily:'Sora,sans-serif', fontSize:'.68rem', fontWeight:600, padding:'4px 8px', borderRadius:99, cursor:'pointer',
                      background: school.libraryEnabled ? '#ecfdf5' : '#f0f2f7',
                      color:      school.libraryEnabled ? '#10c98e' : '#9094a8',
                      border:     `1px solid ${school.libraryEnabled ? '#a7f3d0' : '#e4e7f0'}`}}>
                    Library
                  </button>
                  <button
                    onClick={() => handleModuleToggle(school._id, 'hostel', !!school.hostelEnabled)}
                    style={{fontFamily:'Sora,sans-serif', fontSize:'.68rem', fontWeight:600, padding:'4px 8px', borderRadius:99, cursor:'pointer',
                      background: school.hostelEnabled ? '#ecfdf5' : '#f0f2f7',
                      color:      school.hostelEnabled ? '#10c98e' : '#9094a8',
                      border:     `1px solid ${school.hostelEnabled ? '#a7f3d0' : '#e4e7f0'}`}}>
                    Hostel
                  </button>
                  <button
                    onClick={() => handleModuleToggle(school._id, 'cloPlo', !!school.cloPloEnabled)}
                    style={{fontFamily:'Sora,sans-serif', fontSize:'.68rem', fontWeight:600, padding:'4px 8px', borderRadius:99, cursor:'pointer',
                      background: school.cloPloEnabled ? '#ecfdf5' : '#f0f2f7',
                      color:      school.cloPloEnabled ? '#10c98e' : '#9094a8',
                      border:     `1px solid ${school.cloPloEnabled ? '#a7f3d0' : '#e4e7f0'}`}}>
                    CLO/PLO
                  </button>
                </div>
              </div>
              <div className="ss-card-actions">
                <button
                  className={`btn-toggle-school ${school.isActive!==false?'deactivate':'activate'}`}
                  onClick={() => handleToggle(school._id, school.isActive!==false)}
                  disabled={toggling[school._id]}
                  title={school.isActive!==false ? 'Deactivate School' : 'Activate School'}>
                  {toggling[school._id] ? '…' : school.isActive!==false ? <NoSymbolIcon /> : <CheckCircleIcon />}
                </button>
                <Link to={`/super/schools/${school._id}`} className="manage-link">
                  Manage →
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CREATE SCHOOL MODAL */}
      {showModal && createPortal(
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal-card">
            <div className="modal-header">
              <div>
                <div className="modal-title">Add New School</div>
                <div className="modal-sub">Fill in the details to onboard a new institution.</div>
              </div>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <XMarkIcon />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="field">
                <label>School Name</label>
                <input
                  type="text"
                  placeholder="e.g. Beacon High School"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="field">
                <label>Subdomain</label>
                <input
                  type="text"
                  placeholder="e.g. beacon (→ beacon.yourapp.com)"
                  value={formData.subdomain}
                  onChange={(e) => setFormData({ ...formData, subdomain: e.target.value })}
                  required
                />
              </div>

              <div className="field">
                <label>Package</label>
                <select
                  value={formData.rolePackage}
                  onChange={(e) => setFormData({ ...formData, rolePackage: e.target.value })}
                  required
                  style={{fontFamily:'Sora,sans-serif', fontSize:'.84rem', padding:'9px 12px', borderRadius:10, border:'1.5px solid #e4e7f0', background:'white', color:'#1a1d2e', outline:'none', width:'100%'}}
                >
                  <option value="" disabled>Choose a package…</option>
                  {PACKAGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>

              <div className="field">
                <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',textTransform:'none',fontSize:'.82rem',fontWeight:500,color:'#1a1d2e',marginBottom:0}}>
                  <input
                    type="checkbox"
                    checked={formData.cloPloEnabled}
                    onChange={(e) => setFormData({ ...formData, cloPloEnabled: e.target.checked })}
                    style={{width:16,height:16,cursor:'pointer'}}
                  />
                  Enable CLO/PLO module
                </label>
                <div style={{fontSize:'.72rem',color:'#9094a8',marginTop:4}}>Off by default — can also be toggled later from the schools list.</div>
              </div>

              <div className="field-row">
                <div className="field">
                  <label>Email</label>
                  <input
                    type="email"
                    placeholder="school@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label>Phone</label>
                  <input
                    type="text"
                    placeholder="+92 300 0000000"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
              </div>

              <div className="field">
                <label>Address</label>
                <textarea
                  rows={2}
                  placeholder="Street, City, Country"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                />
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-ghost" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Creating…' : <><PlusIcon /> Create School</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      
      , document.body)}
    </div>
  );
};

export default SuperSchools;